<template>
  <div ref="rootRef" class="relative">
    <!-- Déclencheur : mon picto + mon nom -->
    <button
      type="button"
      class="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-slate-100 dark:hover:bg-alt-dark-raised"
      :aria-expanded="open"
      aria-haspopup="menu"
      @click.stop="open = !open"
    >
      <UserAvatar
        :id="auth.user?.userId"
        :name="auth.user?.displayName ?? ''"
        :has-avatar="auth.user?.hasAvatar"
        size="sm"
      />
      <span class="hidden text-sm text-slate-600 dark:text-alt-dark-muted sm:inline">{{ auth.user?.displayName }}</span>
      <span aria-hidden="true" class="text-xs text-slate-400">▾</span>
    </button>

    <!-- Panneau -->
    <div
      v-if="open"
      role="menu"
      class="absolute right-0 z-50 mt-2 w-72 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-alt-dark-border dark:bg-alt-dark-surface"
    >
      <!-- En-tête : identité + rôles -->
      <div class="flex items-center gap-3 border-b border-slate-100 p-4 dark:border-alt-dark-border">
        <UserAvatar
          :id="auth.user?.userId"
          :name="auth.user?.displayName ?? ''"
          :has-avatar="auth.user?.hasAvatar"
          size="md"
        />
        <div class="min-w-0">
          <p class="truncate font-medium text-space-indigo dark:text-altoneo-50">{{ auth.user?.displayName }}</p>
          <div class="mt-0.5 flex flex-wrap gap-1">
            <span v-for="r in auth.roles" :key="r" class="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500 dark:bg-alt-dark-raised dark:text-alt-dark-muted">
              {{ ROLE_LABELS[r] }}
            </span>
            <span v-if="auth.roles.length === 0" class="text-[10px] text-slate-400">Aucun rôle</span>
          </div>
        </div>
      </div>

      <!-- Mes informations : photo -->
      <div class="border-b border-slate-100 p-3 dark:border-alt-dark-border">
        <p class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Mes informations</p>
        <input ref="avatarInput" type="file" accept="image/*" class="hidden" @change="onAvatarPick" />
        <div class="flex gap-2">
          <AltButton size="sm" variant="secondary" :loading="avatarBusy" @click="avatarInput?.click()">
            {{ auth.user?.hasAvatar ? 'Changer ma photo' : 'Ajouter une photo' }}
          </AltButton>
          <AltButton v-if="auth.user?.hasAvatar" size="sm" variant="ghost" :disabled="avatarBusy" @click="removeAvatar">
            Retirer
          </AltButton>
        </div>
      </div>

      <!-- Préférences : thème + contraste -->
      <div class="border-b border-slate-100 p-3 dark:border-alt-dark-border">
        <p class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Préférences</p>
        <label class="mb-1 block text-xs text-slate-500 dark:text-alt-dark-muted">Thème par défaut</label>
        <div class="mb-3 flex gap-1">
          <button
            v-for="opt in themeOptions"
            :key="opt.value"
            type="button"
            class="flex-1 rounded-md border px-2 py-1 text-xs transition-colors"
            :class="themePreference === opt.value
              ? 'border-space-indigo bg-space-indigo text-white'
              : 'border-slate-200 text-slate-500 hover:bg-slate-50 dark:border-alt-dark-border dark:text-alt-dark-muted dark:hover:bg-alt-dark-raised'"
            @click="setTheme(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
        <label class="mb-1 block text-xs text-slate-500 dark:text-alt-dark-muted">Taille du texte</label>
        <div class="mb-3 flex gap-1">
          <button
            v-for="opt in fontOptions"
            :key="opt.value"
            type="button"
            class="flex-1 rounded-md border px-2 py-1 text-xs transition-colors"
            :class="fontScale === opt.value
              ? 'border-space-indigo bg-space-indigo text-white'
              : 'border-slate-200 text-slate-500 hover:bg-slate-50 dark:border-alt-dark-border dark:text-alt-dark-muted dark:hover:bg-alt-dark-raised'"
            @click="setFontScale(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
        <label class="flex items-center justify-between text-xs text-slate-600 dark:text-alt-dark-muted">
          <span>Contraste élevé</span>
          <AltSwitch :model-value="highContrast" @update:model-value="setHighContrast" />
        </label>
      </div>

      <!--
        L'APPLICATION. La porte PERMANENTE de l'installation : le bandeau se
        refuse une fois pour toutes, et sans cette entrée on ne pourrait plus
        jamais revenir dessus sans vider le stockage du navigateur.
      -->
      <div
        v-if="estInstallee || modeInstallation !== 'aucun'"
        class="border-b border-slate-100 p-3 dark:border-alt-dark-border"
      >
        <p class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Application</p>
        <p v-if="estInstallee" class="text-xs text-slate-500 dark:text-alt-dark-muted">
          Installée sur cet appareil.
        </p>
        <template v-else-if="modeInstallation === 'invitation'">
          <p class="mb-2 text-xs leading-relaxed text-slate-500 dark:text-alt-dark-muted">
            Une icône sur votre bureau et une fenêtre à part, sans barre d'adresse.
          </p>
          <AltButton size="sm" variant="secondary" :loading="installationEnCours" @click="installerApp">
            Installer l'application
          </AltButton>
        </template>
        <p v-else class="text-xs leading-relaxed text-slate-500 dark:text-alt-dark-muted">
          Sur iPhone et iPad&nbsp;:
          <span class="font-medium text-slate-600 dark:text-alt-dark-muted">Partager</span>
          <span aria-hidden="true"> → </span>
          <span class="font-medium text-slate-600 dark:text-alt-dark-muted">Sur l'écran d'accueil</span>.
        </p>
      </div>

      <!-- Accès API (0113) : des jetons pour que Claude ou un script agissent
           en son nom. Rangé ici parce qu'il appartient à la personne, comme sa
           photo et ses préférences — personne d'autre ne les voit. -->
      <button
        type="button"
        role="menuitem"
        class="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-600 hover:bg-slate-50 dark:text-alt-dark-muted dark:hover:bg-alt-dark-raised"
        @click="ouvrirJetons"
      >
        <span aria-hidden="true">🔑</span> Accès API (jetons)
      </button>

      <!-- Aide -->
      <RouterLink
        to="/aide"
        role="menuitem"
        class="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 dark:text-alt-dark-muted dark:hover:bg-alt-dark-raised"
        @click="open = false"
      >
        <span aria-hidden="true">📘</span> Aide & guides
      </RouterLink>

      <!-- Déconnexion -->
      <button
        type="button"
        role="menuitem"
        class="flex w-full items-center gap-2 border-t border-slate-100 px-4 py-2.5 text-left text-sm text-amaranth hover:bg-slate-50 dark:border-alt-dark-border dark:hover:bg-alt-dark-raised"
        @click="logout"
      >
        <span aria-hidden="true">⏻</span> Déconnexion
      </button>
    </div>
    <JetonsApiModal v-model:open="jetonsOuverts" />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter, RouterLink } from 'vue-router';
import { useToast } from 'altoneo-charting';
import { ROLE_LABELS } from '@its/shared';
import { useAuthStore } from '@/stores/auth';
import { api, ApiError } from '@/lib/api';
import { bumpAvatarVersion } from '@/lib/avatar';
import UserAvatar from '@/components/UserAvatar.vue';
import JetonsApiModal from '@/components/JetonsApiModal.vue';
import {
  themePreference,
  highContrast,
  fontScale,
  setTheme,
  setHighContrast,
  setFontScale,
  type ThemePreference,
  type FontScale,
} from '@/lib/preferences';
import { modeInstallation, estInstallee, installationEnCours, installer } from '@/lib/installation';

const auth = useAuthStore();
const router = useRouter();
const toast = useToast();

const open = ref(false);
const jetonsOuverts = ref(false);

function ouvrirJetons(): void {
  open.value = false;
  jetonsOuverts.value = true;
}
const rootRef = ref<HTMLElement | null>(null);
const avatarInput = ref<HTMLInputElement | null>(null);
const avatarBusy = ref(false);

const themeOptions: { value: ThemePreference; label: string }[] = [
  { value: 'clair', label: 'Clair' },
  { value: 'sombre', label: 'Sombre' },
  { value: 'systeme', label: 'Système' },
];
const fontOptions: { value: FontScale; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'confort', label: 'Confort' },
  { value: 'grand', label: 'Grand' },
];

async function onAvatarPick(e: Event): Promise<void> {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file) return;
  avatarBusy.value = true;
  try {
    await api.upload('/me/avatar', file);
    if (auth.user) auth.user.hasAvatar = true;
    bumpAvatarVersion();
    toast.success('Photo mise à jour');
  } catch (err) {
    toast.error(err instanceof ApiError ? err.message : 'Téléversement impossible');
  } finally {
    avatarBusy.value = false;
    if (avatarInput.value) avatarInput.value.value = '';
  }
}

async function removeAvatar(): Promise<void> {
  avatarBusy.value = true;
  try {
    await api.del('/me/avatar');
    if (auth.user) auth.user.hasAvatar = false;
    bumpAvatarVersion();
    toast.success('Photo retirée');
  } catch (err) {
    toast.error(err instanceof ApiError ? err.message : 'Suppression impossible');
  } finally {
    avatarBusy.value = false;
  }
}

async function installerApp(): Promise<void> {
  const ok = await installer();
  if (ok) {
    open.value = false;
    toast.success('Application installée');
  }
}

async function logout(): Promise<void> {
  open.value = false;
  await auth.logout();
  await router.push({ name: 'login' });
}

// Fermeture au clic extérieur et à Échap.
function onDocClick(e: MouseEvent): void {
  if (open.value && rootRef.value && !rootRef.value.contains(e.target as Node)) open.value = false;
}
function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') open.value = false;
}
onMounted(() => {
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick);
  document.removeEventListener('keydown', onKey);
});
</script>
